import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";

import NavBar from "../components/common/navBar";
import Footer from "../components/common/footer";

import { useUserInfo } from "../hooks/useUserInfo";
import SEO from "../data/seo";
import { appStats, getTotalMonthlyPageViews } from "../data/services";
import { CONTACT_EMAIL } from "../data/constants";
import { useTheme } from "../contexts/ThemeContext";
import { useLanguage } from "../contexts/LanguageContext";
import { Theme } from "../theme";

const fadeInUp = keyframes`
	from {
		opacity: 0;
		transform: translateY(30px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
`;

const PageContainer = styled.div<{ theme: Theme }>`
	min-height: 100vh;
	background: ${props => props.theme.colors.background.primary};
	transition: background-color 0.3s ease;
`;

const Container = styled.div`
	max-width: 1100px;
	margin: 0 auto;
	padding: 3rem 2rem 4rem 2rem;

	@media (max-width: 768px) {
		padding: 2rem 1rem 3rem 1rem;
	}
`;

const BackLink = styled(Link)<{ theme: Theme }>`
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
	color: ${props => props.theme.colors.text.secondary};
	text-decoration: none;
	margin-bottom: 2rem;
	font-size: 1rem;
	transition: color 0.2s ease;
	animation: ${fadeInUp} 0.3s ease-out;

	&:hover {
		color: ${props => props.theme.colors.primary.main};
	}

	&::before {
		content: '←';
	}
`;

const Header = styled.div`
	margin-bottom: 4rem;
	animation: ${fadeInUp} 0.3s ease-out 0.1s both;
`;

const Title = styled.h1<{ theme: Theme }>`
	font-size: ${props => props.theme.typography.roles.pageHeading.fontSize};
	font-weight: ${props => props.theme.typography.roles.pageHeading.fontWeight};
	line-height: ${props => props.theme.typography.roles.pageHeading.lineHeight};
	letter-spacing: ${props => props.theme.typography.roles.pageHeading.letterSpacing};
	color: ${props => props.theme.colors.text.primary};
	margin-bottom: 0.75rem;

	@media (max-width: 768px) {
		letter-spacing: -0.015em;
	}
`;

const Subtitle = styled.p<{ theme: Theme }>`
	font-size: 1.25rem;
	color: ${props => props.theme.colors.text.secondary};
	line-height: 1.8;

	@media (max-width: 768px) {
		font-size: 1.125rem;
	}
`;

const Section = styled.section`
	margin-bottom: 4rem;
	animation: ${fadeInUp} 0.3s ease-out 0.2s both;
`;

const SectionTitle = styled.h2<{ theme: Theme }>`
	font-size: 2rem;
	font-weight: 700;
	color: ${props => props.theme.colors.text.primary};
	margin-bottom: 2rem;

	@media (max-width: 768px) {
		font-size: 1.75rem;
	}
`;

const Description = styled.p<{ theme: Theme }>`
	font-size: 1.125rem;
	color: ${props => props.theme.colors.text.secondary};
	line-height: 1.8;
	margin-bottom: 2rem;
`;

const FeaturesGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
	gap: 1.5rem;
	margin-bottom: 2rem;

	@media (max-width: 768px) {
		grid-template-columns: 1fr;
	}
`;

const FeatureCard = styled.div<{ theme: Theme }>`
	background: ${props => props.theme.colors.background.secondary};
	border: 1px solid ${props => props.theme.colors.neutral[200]};
	border-radius: 1rem;
	padding: 1.5rem;
	transition: all 0.2s ease;

	&:hover {
		box-shadow: ${props => props.theme.shadows.md};
		border-color: ${props => props.theme.colors.primary.main};
	}
`;

const FeatureTitle = styled.h3<{ theme: Theme }>`
	font-size: 1.25rem;
	font-weight: 600;
	color: ${props => props.theme.colors.text.primary};
	margin-bottom: 0.75rem;
`;

const FeatureDescription = styled.p<{ theme: Theme }>`
	font-size: 1rem;
	color: ${props => props.theme.colors.text.secondary};
	line-height: 1.6;
`;

const StatsTable = styled.div<{ theme: Theme }>`
	background: ${props => props.theme.colors.background.secondary};
	border: 1px solid ${props => props.theme.colors.neutral[200]};
	border-radius: 1rem;
	overflow: hidden;
	margin-bottom: 1rem;
`;

const TableHeader = styled.div<{ theme: Theme }>`
	display: grid;
	grid-template-columns: 2fr 1fr 1fr;
	gap: 1rem;
	padding: 1.25rem 1.5rem;
	background: ${props => props.theme.mode === 'light'
		? props.theme.colors.neutral[100]
		: props.theme.colors.neutral[200]};
	font-weight: 600;
	color: ${props => props.theme.colors.text.primary};
	font-size: 0.9rem;

	@media (max-width: 768px) {
		grid-template-columns: 2fr 1fr;
		padding: 1rem;
		font-size: 0.85rem;
	}
`;

const TableRow = styled.div<{ theme: Theme }>`
	display: grid;
	grid-template-columns: 2fr 1fr 1fr;
	gap: 1rem;
	padding: 1.25rem 1.5rem;
	border-top: 1px solid ${props => props.theme.colors.neutral[200]};
	transition: background-color 0.2s ease;

	&:hover {
		background: ${props => props.theme.mode === 'light'
			? props.theme.colors.neutral[100]
			: props.theme.colors.neutral[200]};
	}

	@media (max-width: 768px) {
		grid-template-columns: 2fr 1fr;
		padding: 1rem;
	}
`;

const TableCell = styled.div<{ theme: Theme }>`
	color: ${props => props.theme.colors.text.primary};
	font-size: 1rem;

	@media (max-width: 768px) {
		font-size: 0.9rem;
	}
`;

const TableCellSecondary = styled(TableCell)`
	color: ${props => props.theme.colors.text.secondary};
	font-size: 0.95rem;

	@media (max-width: 768px) {
		font-size: 0.85rem;
	}
`;

const TableCellHidden = styled(TableCell)`
	@media (max-width: 768px) {
		display: none;
	}
`;

const TotalRow = styled(TableRow)`
	background: ${props => props.theme.mode === 'light'
		? props.theme.colors.neutral[100]
		: props.theme.colors.neutral[200]};
	font-weight: 600;
`;

const Note = styled.p<{ theme: Theme }>`
	font-size: 0.9rem;
	color: ${props => props.theme.colors.text.secondary};
	font-style: italic;
	margin-top: 0.5rem;
`;

const ContactBox = styled.div<{ theme: Theme }>`
	background: ${props => props.theme.colors.background.secondary};
	border: 2px solid ${props => props.theme.colors.primary.main};
	border-radius: 1rem;
	padding: 2.5rem;
	text-align: center;

	@media (max-width: 768px) {
		padding: 2rem 1.5rem;
	}
`;

const EmailText = styled.p<{ theme: Theme }>`
	font-size: 1.125rem;
	color: ${props => props.theme.colors.text.secondary};
	margin-bottom: 1.5rem;
	line-height: 1.6;
`;

const EmailLink = styled.a<{ theme: Theme }>`
	display: inline-block;
	background: ${props => props.theme.colors.primary.gradient};
	color: white;
	padding: 1rem 2.5rem;
	border-radius: 0.75rem;
	text-decoration: none;
	font-weight: 600;
	font-size: 1.125rem;
	transition: all 0.2s ease;
	box-shadow: ${props => props.theme.shadows.md};

	&:hover {
		transform: translateY(-2px);
		box-shadow: ${props => props.theme.shadows.lg};
	}

	&:active {
		transform: translateY(0);
	}

	@media (max-width: 768px) {
		padding: 0.875rem 2rem;
		font-size: 1rem;
	}
`;

const ContactNote = styled(Note)`
	margin-top: 1.5rem;
`;

const FooterWrapper = styled.div`
	margin-top: 6rem;
`;

const formatNumber = (num: number): string => {
	return num.toLocaleString();
};

const AdSpaces = () => {
	const { theme } = useTheme();
	const { t, language } = useLanguage();
	const userInfo = useUserInfo();

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	const currentSEO = SEO.find((item) => item.page === "ad-spaces") || SEO[0];
	const totalPageViews = getTotalMonthlyPageViews();

	const emailSubject = language === 'ja'
		? '広告枠に関するお問い合わせ'
		: 'Inquiry about Ad Spaces';
	const emailBody = language === 'ja'
		? 'お名前：\n会社名：\n\nご希望の広告枠：\n\nご予算：\n\nその他ご要望：\n'
		: 'Name:\nCompany:\n\nDesired Ad Space:\n\nBudget:\n\nAdditional Requirements:\n';

	return (
		<React.Fragment>
			<Helmet>
				<title>{`${t.adSpaces.title} | ${userInfo.main.title}`}</title>
				<meta name="description" content={currentSEO.description} />
				<meta name="keywords" content={currentSEO.keywords.join(", ")} />
			</Helmet>

			<PageContainer theme={theme}>
				<NavBar active="services" />
				<Container>
					<BackLink to="/services" theme={theme}>
						{t.adSpaces.backToServices}
					</BackLink>

					<Header>
						<Title theme={theme}>{t.adSpaces.title}</Title>
						<Subtitle theme={theme}>{t.adSpaces.subtitle}</Subtitle>
					</Header>

					<Section>
						<Description theme={theme}>
							{t.adSpaces.description}
						</Description>
					</Section>

					<Section>
						<SectionTitle theme={theme}>
							{t.adSpaces.features.title}
						</SectionTitle>
						<FeaturesGrid>
							{t.adSpaces.features.items.map((feature, index) => (
								<FeatureCard key={index} theme={theme}>
									<FeatureTitle theme={theme}>{feature.title}</FeatureTitle>
									<FeatureDescription theme={theme}>
										{feature.description}
									</FeatureDescription>
								</FeatureCard>
							))}
						</FeaturesGrid>
					</Section>

					<Section>
						<SectionTitle theme={theme}>
							{t.adSpaces.stats.title}
						</SectionTitle>
						<StatsTable theme={theme}>
							<TableHeader theme={theme}>
								<div>{language === 'ja' ? 'アプリ名' : 'App Name'}</div>
								<TableCellHidden theme={theme}>
									{language === 'ja' ? 'カテゴリ' : 'Category'}
								</TableCellHidden>
								<div>{language === 'ja' ? '月間PV' : 'Monthly PV'}</div>
							</TableHeader>
							{appStats.map((app, index) => (
								<TableRow key={index} theme={theme}>
									<TableCell theme={theme}>
										{language === 'ja' ? app.name : app.nameEn}
									</TableCell>
									<TableCellHidden theme={theme}>
										<TableCellSecondary theme={theme}>
											{language === 'ja' ? app.category : app.categoryEn}
										</TableCellSecondary>
									</TableCellHidden>
									<TableCell theme={theme}>
										{formatNumber(app.monthlyPageViews)}
									</TableCell>
								</TableRow>
							))}
							<TotalRow theme={theme}>
								<TableCell theme={theme}>
									{language === 'ja' ? '合計' : 'Total'}
								</TableCell>
								<TableCellHidden theme={theme} />
								<TableCell theme={theme}>
									{formatNumber(totalPageViews)}
								</TableCell>
							</TotalRow>
						</StatsTable>
						<Note theme={theme}>{t.adSpaces.stats.note}</Note>
					</Section>

					<Section>
						<SectionTitle theme={theme}>
							{t.adSpaces.contact.title}
						</SectionTitle>
						<ContactBox theme={theme}>
							<EmailText theme={theme}>
								{t.adSpaces.contact.description}
							</EmailText>
							<EmailLink
								href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`}
								theme={theme}
							>
								{t.adSpaces.contact.emailButton}
							</EmailLink>
							<ContactNote theme={theme}>
								{t.adSpaces.contact.note}
							</ContactNote>
						</ContactBox>
					</Section>

					<FooterWrapper>
						<Footer />
					</FooterWrapper>
				</Container>
			</PageContainer>
		</React.Fragment>
	);
};

export default AdSpaces;
